import React, { useMemo, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import { Search, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { MOCK_PRODUCTS, MOCK_BRANCHES } from '../../utils/mockData';
import { useToast } from '../../components/ui/Toast';
interface StockRow {
  id: string;
  branchId: string;
  branchCode: string;
  branchName: string;
  itemName: string;
  itemNameAr: string;
  sku: string;
  genericCode: string;
  tradeCode: string;
  stock: number;
}
const ROWS_PER_PAGE = 10;
// Generate flattened stock rows
const STOCK_ROWS: StockRow[] = (() => {
  const rows: StockRow[] = [];
  let seed = 42;
  const seededRandom = () => {
    seed = seed * 16807 % 2147483647;
    return (seed - 1) / 2147483646;
  };
  MOCK_PRODUCTS.forEach((product, pIdx) => {
    MOCK_BRANCHES.forEach((branch, bIdx) => {
      const override = product.branchOverrides?.[branch.id];
      const stock = override?.stock ?? Math.floor(seededRandom() * 180);
      rows.push({
        id: `${product.id}-${branch.id}`,
        branchId: branch.id,
        branchCode: branch.code,
        branchName: branch.name,
        itemName: product.nameEn,
        itemNameAr: product.nameAr || '',
        sku: product.sku,
        genericCode: product.genericCode,
        tradeCode: product.tradeCode,
        stock
      });
    });
  });
  return rows;
})();
export function Inventory() {
  const {
    showToast
  } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [branchFilter, setBranchFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState<StockRow | null>(null);
  const branchOptions = [{
    value: 'all',
    label: 'All Branches'
  }, ...MOCK_BRANCHES.map(b => ({
    value: b.id,
    label: `${b.code} — ${b.name}`
  }))];
  const filteredRows = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return STOCK_ROWS.filter(row => {
      const matchesBranch = branchFilter === 'all' || row.branchId === branchFilter;
      const matchesSearch = !q || row.branchCode.toLowerCase().includes(q) || row.branchName.toLowerCase().includes(q) || row.itemName.toLowerCase().includes(q) || row.sku.toLowerCase().includes(q) || row.genericCode.toLowerCase().includes(q) || row.tradeCode.toLowerCase().includes(q);
      return matchesBranch && matchesSearch;
    });
  }, [searchQuery, branchFilter]);
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / ROWS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedRows = filteredRows.slice((safePage - 1) * ROWS_PER_PAGE, safePage * ROWS_PER_PAGE);
  const startRow = (safePage - 1) * ROWS_PER_PAGE + 1;
  const endRow = Math.min(safePage * ROWS_PER_PAGE, filteredRows.length);
  const handleSearch = (val: string) => {
    setSearchQuery(val);
    setCurrentPage(1);
  };
  const handleBranchFilter = (val: string) => {
    setBranchFilter(val);
    setCurrentPage(1);
  };
  const handleExport = () => {
    showToast({
      type: 'success',
      title: 'Export Started',
      message: `Exporting ${filteredRows.length} rows as Excel...`
    });
  };
  const getStockBadge = (stock: number) => {
    if (stock === 0) return <Badge variant="danger" size="sm" data-id="element-724">
          Out of Stock
        </Badge>;
    if (stock < 10) return <Badge variant="warning" size="sm" data-id="element-725">
          Low Stock
        </Badge>;
    return <Badge variant="success" size="sm" data-id="element-726">
        In Stock
      </Badge>;
  };
  const getStockColor = (stock: number) => {
    if (stock === 0) return 'text-red-600 font-semibold';
    if (stock < 10) return 'text-amber-600 font-semibold';
    return 'text-slate-900 font-semibold';
  };
  return <div className="space-y-6 pb-12" data-id="element-727">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-728">
        <div data-id="element-729">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-730">
            Inventory Management
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-731">
            Real-time stock levels per branch and item
          </p>
        </div>
        <Button variant="outline" onClick={handleExport} data-id="element-732">
          <Download className="w-4 h-4 mr-2" data-id="element-733" />
          Export Excel
        </Button>
      </div>

      {/* Filters */}
      <Card data-id="element-734">
        <CardContent className="pt-6" data-id="element-735">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-736">
            <div className="relative flex-1" data-id="element-737">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-738" />
              <Input placeholder="Search by branch, item name, SKU, generic code, or trade code..." value={searchQuery} onChange={e => handleSearch(e.target.value)} className="pl-9" data-id="element-739" />
            </div>
            <div className="w-full sm:w-64" data-id="element-740">
              <Select value={branchFilter} onChange={e => handleBranchFilter(e.target.value)} options={branchOptions} data-id="element-741" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card data-id="element-742">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50 px-6 py-4" data-id="element-743">
          <div className="flex items-center justify-between" data-id="element-744">
            <CardTitle className="text-base font-semibold text-slate-900" data-id="element-745">
              Stock Inventory
            </CardTitle>
            <span className="text-xs text-slate-500 font-medium" data-id="element-746">
              {filteredRows.length} rows total
            </span>
          </div>
        </CardHeader>
        <CardContent className="p-0" data-id="element-747">
          <div className="overflow-x-auto" data-id="element-748">
            <Table data-id="element-749">
              <TableHeader data-id="element-750">
                <TableRow data-id="element-751">
                  <TableHead data-id="element-752">Branch Code</TableHead>
                  <TableHead className="w-[22%]" data-id="element-753">Item Name</TableHead>
                  <TableHead data-id="element-754">SKU</TableHead>
                  <TableHead data-id="element-755">Generic Code</TableHead>
                  <TableHead data-id="element-756">Trade Code</TableHead>
                  <TableHead className="text-right" data-id="element-757">Stock</TableHead>
                  <TableHead className="text-right" data-id="element-758">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-id="element-759">
                {paginatedRows.length > 0 ? paginatedRows.map(row => <TableRow key={row.id} className="hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => setSelectedRow(row)} data-id="element-760">
                      <TableCell data-id="element-761">
                        <code className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-mono border border-slate-200" data-id="element-762">
                          {row.branchCode}
                        </code>
                      </TableCell>
                      <TableCell data-id="element-763">
                        <div className="font-medium text-sm text-slate-900" data-id="element-764">
                          {row.itemName}
                        </div>
                        {row.itemNameAr && <div className="text-xs text-slate-400" data-id="element-765">
                            {row.itemNameAr}
                          </div>}
                      </TableCell>
                      <TableCell data-id="element-766">
                        <code className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-mono border border-slate-200" data-id="element-767">
                          {row.sku}
                        </code>
                      </TableCell>
                      <TableCell data-id="element-768">
                        <code className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-mono border border-slate-200" data-id="element-769">
                          {row.genericCode}
                        </code>
                      </TableCell>
                      <TableCell data-id="element-770">
                        <code className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-mono border border-slate-200" data-id="element-771">
                          {row.tradeCode}
                        </code>
                      </TableCell>
                      <TableCell className="text-right" data-id="element-772">
                        <span className={getStockColor(row.stock)} data-id="element-773">
                          {row.stock}
                        </span>
                      </TableCell>
                      <TableCell className="text-right" data-id="element-774">
                        {getStockBadge(row.stock)}
                      </TableCell>
                    </TableRow>) : <TableRow data-id="element-775">
                    <TableCell colSpan={7} className="h-32 text-center" data-id="element-776">
                      <div className="flex flex-col items-center justify-center text-slate-500" data-id="element-777">
                        <Search className="w-8 h-8 mb-2 text-slate-300" data-id="element-778" />
                        <p className="text-sm font-medium text-slate-900" data-id="element-779">
                          No results found
                        </p>
                        <p className="text-sm text-slate-500 mt-1" data-id="element-780">
                          Try adjusting your search or filter.
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {filteredRows.length > 0 && <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100" data-id="element-781">
              <p className="text-sm text-slate-500" data-id="element-782">
                Showing{' '}
                <span className="font-medium text-slate-700" data-id="element-783">{startRow}</span>{' '}
                to <span className="font-medium text-slate-700" data-id="element-784">{endRow}</span>{' '}
                of{' '}
                <span className="font-medium text-slate-700" data-id="element-785">
                  {filteredRows.length}
                </span>{' '}
                rows
              </p>
              <div className="flex items-center gap-2" data-id="element-786">
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={safePage <= 1} data-id="element-787">
                  <ChevronLeft className="w-4 h-4 mr-1" data-id="element-788" />
                  Previous
                </Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} data-id="element-789">
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" data-id="element-790" />
                </Button>
              </div>
            </div>}
        </CardContent>
      </Card>

      {/* Item Detail Modal */}
      <Modal isOpen={!!selectedRow} onClose={() => setSelectedRow(null)} title="Item Stock Details" maxWidth="max-w-lg" data-id="element-791">
        {selectedRow && <div className="space-y-5" data-id="element-792">
            <div data-id="element-793">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1" data-id="element-794">
                Item Name
              </p>
              <p className="text-base font-semibold text-slate-900" data-id="element-795">
                {selectedRow.itemName}
              </p>
              {selectedRow.itemNameAr && <p className="text-sm text-slate-500 mt-0.5" dir="rtl" data-id="element-796">
                  {selectedRow.itemNameAr}
                </p>}
            </div>

            <div className="grid grid-cols-2 gap-4" data-id="element-797">
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100" data-id="element-798">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1" data-id="element-799">
                  SKU
                </p>
                <code className="text-sm font-mono font-semibold text-slate-900" data-id="element-800">
                  {selectedRow.sku}
                </code>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100" data-id="element-801">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1" data-id="element-802">
                  Branch Code
                </p>
                <code className="text-sm font-mono font-semibold text-slate-900" data-id="element-803">
                  {selectedRow.branchCode}
                </code>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100" data-id="element-804">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1" data-id="element-805">
                  Generic Code
                </p>
                <code className="text-sm font-mono font-semibold text-slate-900" data-id="element-806">
                  {selectedRow.genericCode}
                </code>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100" data-id="element-807">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1" data-id="element-808">
                  Trade Code
                </p>
                <code className="text-sm font-mono font-semibold text-slate-900" data-id="element-809">
                  {selectedRow.tradeCode}
                </code>
              </div>
            </div>

            <div className="bg-slate-50 rounded-lg p-3 border border-slate-100" data-id="element-810">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1" data-id="element-811">
                Branch
              </p>
              <p className="text-sm font-medium text-slate-900" data-id="element-812">
                {selectedRow.branchName}
              </p>
              <p className="text-xs text-slate-500 mt-0.5" data-id="element-813">
                Code: {selectedRow.branchCode}
              </p>
            </div>

            <div className="flex items-center justify-between bg-slate-50 rounded-lg p-4 border border-slate-100" data-id="element-814">
              <div data-id="element-815">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1" data-id="element-816">
                  Current Stock
                </p>
                <p className={`text-2xl font-bold ${getStockColor(selectedRow.stock)}`} data-id="element-817">
                  {selectedRow.stock}
                </p>
              </div>
              <div data-id="element-818">{getStockBadge(selectedRow.stock)}</div>
            </div>

            <div className="flex justify-end pt-2" data-id="element-819">
              <Button variant="outline" onClick={() => setSelectedRow(null)} data-id="element-820">
                Close
              </Button>
            </div>
          </div>}
      </Modal>
    </div>;
}